import { useId } from "react";
import type { CloseHandover, CloseHandoverItem, CloseHandoverRow, CloseSavedSearchDecision, CloseSavedSearchReason } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { useTab } from "../api/queries";
import { Button } from "../design/components/Button";
import { DataTable } from "../design/components/DataTable";
import { useToast } from "../design/components/Toast";
import { Reason } from "../design/components/Reason";
import { StatusPill } from "../design/components/StatusPill";
import { messageForError } from "../i18n/errors";
import { formatCount } from "../i18n/format";
import { useLocale, useT } from "../i18n/locale";
import { useAccess } from "../session/access";
import { whenParts } from "./close-import-format";
import { closeHandoverQuery, useCloseSavedSearchDecision } from "./close-import-queries";

// «Pronti a spegnere Close?» (M8, la verifica del passaggio, 22/10/2026), sotto «Ogni sera da Close», soltanto per il Super admin di
// Gioia365 (il server risponde soltanto a lui). In cima il giudizio: «Tutto a posto» oppure «Non ancora» con l'elenco di che cosa manca
// prima della disdetta, in parole comuni e il più importante per primo (con quello che Lucio deve decidere); poi, risorsa per risorsa, quanti oggetti ci sono in Close, quanti
// in Gioia365 e la differenza con il suo motivo (in evidenza quella senza); infine l'audio delle chiamate e gli allegati delle note
// (INGR-27), con lo stato della loro copia. Soltanto numeri: nessun
// dato dei clienti né testo di Close (INGR-17). Vale dopo Close in sola lettura e l'ultima importazione: vede soltanto ciò che Gioia365
// ha letto (INGR-28). Le date nel fuso dell'azienda, come il resto della pagina.

export function CloseHandoverSection() {
  const t = useT();
  const tab = useTab();
  const titleId = useId();
  const handover = useQuery(closeHandoverQuery(tab));
  return (
    <section className="g-ci__set g-ci__handover" aria-labelledby={titleId}>
      <div className="g-ci__set-title">
        <h2 id={titleId}>{t("closeImport.handover.title")}</h2>
        <p className="g-ci__muted">{t("closeImport.handover.intro")}</p>
      </div>
      {handover.data ? (
        <Handover data={handover.data} />
      ) : handover.isError ? (
        <div className="g-ci__problem">
          <Reason tone="warning" role="alert">
            {messageForError(t, handover.error)}
          </Reason>
          <Button onClick={() => void handover.refetch()}>{t("common.retry")}</Button>
        </div>
      ) : (
        <p className="g-ci__muted">{t("common.loading")}</p>
      )}
    </section>
  );
}

function Handover({ data }: { data: CloseHandover }) {
  const t = useT();
  const access = useAccess();
  const timeZone = access.context.settings.timeZone;
  return (
    <>
      {data.run ? <p>{t("closeImport.handover.base", whenParts(timeZone, data.run.finishedAt))}</p> : null}
      <Verdict data={data} />
      {data.rows.length > 0 ? <Rows rows={data.rows} /> : null}
      <SavedSearchNames
        left={data.savedSearchesLeft.length > 0 ? data.savedSearchesLeft : data.savedSearchNames.map((name) => ({ id: null, name, reason: null, decision: null }))}
        total={
          data.rows
            .find((row) => row.resource === "savedSearches")
            ?.explained.filter((item) => item.reason === "counted_only" || item.reason === "decided")
            .reduce((sum, item) => sum + item.count, 0) ?? data.savedSearchNames.length
        }
      />
      <CopyFacts
        title={t("closeImport.handover.audioTitle")}
        off={data.audio.copyOn ? null : t("closeImport.handover.audioOff")}
        facts={[
          { key: "audioWithAudio", value: data.audio.withAudio },
          { key: "audioCopied", value: data.audio.copied },
          { key: "audioToCopy", value: data.audio.toCopy },
          { key: "audioFailed", value: data.audio.failed },
          { key: "audioMissing", value: data.audio.missing },
        ]}
      />
      <CopyFacts
        title={t("closeImport.handover.attachmentsTitle")}
        off={data.attachments.copyOn ? null : t("closeImport.handover.attachmentsOff")}
        facts={[
          { key: "attachmentsTotal", value: data.attachments.total },
          { key: "attachmentsCopied", value: data.attachments.copied },
          { key: "attachmentsToCopy", value: data.attachments.toCopy },
          { key: "attachmentsFailed", value: data.attachments.failed },
          { key: "attachmentsMissing", value: data.attachments.missing },
        ]}
      />
      <p className="g-ci__muted">{t("closeImport.handover.after")}</p>
    </>
  );
}

function Verdict({ data }: { data: CloseHandover }) {
  const t = useT();
  if (data.verdict.ready) {
    return (
      <div className="g-ci__verdict" data-ready="true">
        <StatusPill tone="success" icon="check">
          {t("closeImport.handover.ready")}
        </StatusPill>
        <p>{t("closeImport.handover.readyText")}</p>
      </div>
    );
  }
  return (
    <div className="g-ci__verdict" data-ready="false">
      <StatusPill tone="warning" icon="alert">
        {t("closeImport.handover.notReady")}
      </StatusPill>
      <p>{t("closeImport.handover.notReadyText")}</p>
      <ul>
        {data.verdict.items.map((item, index) => (
          <li key={`${item.code}-${item.resource ?? ""}-${index}`}>
            <ItemText item={item} finishedAt={data.run?.finishedAt ?? null} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function ItemText({ item, finishedAt }: { item: CloseHandoverItem; finishedAt: string | null }) {
  const t = useT();
  const { locale } = useLocale();
  const access = useAccess();
  const count = formatCount(locale, item.count);
  const resource = item.resource === null ? "" : t(`closeImport.handover.resource.${item.resource}`);
  if (item.code === "import_old") {
    const when = finishedAt === null ? { date: "", time: "" } : whenParts(access.context.settings.timeZone, finishedAt);
    return <>{t("closeImport.handover.item.import_old", when)}</>;
  }
  // Le ricerche salvate dicono che cosa c'è da decidere, con i nomi sotto la tabella.
  if (item.code === "counted_only" && item.resource === "savedSearches") return <>{t("closeImport.handover.item.counted_only_savedSearches", { count })}</>;
  return <>{t(`closeImport.handover.item.${item.code}`, { count, resource })}</>;
}

function Rows({ rows }: { rows: readonly CloseHandoverRow[] }) {
  const t = useT();
  const { locale } = useLocale();
  const hints: ReadonlySet<CloseHandoverRow["resource"]> = new Set(["events", "openTasks", "savedSearches", "people"]);
  return (
    <DataTable caption={t("closeImport.handover.caption")} captionHidden>
      <thead>
        <tr>
          <th scope="col">{t("closeImport.handover.what")}</th>
          <th scope="col" className="g-ci__num">
            {t("closeImport.handover.inClose")}
          </th>
          <th scope="col" className="g-ci__num">
            {t("closeImport.handover.inGioia365")}
          </th>
          <th scope="col">{t("closeImport.handover.difference")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.resource} data-unexplained={row.unexplained > 0 ? "true" : undefined}>
            <th scope="row">
              <strong>{t(`closeImport.handover.resource.${row.resource}`)}</strong>
              {hints.has(row.resource) ? <span className="g-table__sub">{t(`closeImport.handover.resourceHint.${row.resource as "events" | "openTasks" | "savedSearches" | "people"}`)}</span> : null}
            </th>
            <td className="g-ci__num">{formatCount(locale, row.close)}</td>
            <td className="g-ci__num">{row.gioia365 === null ? <span className="g-ci__muted">{t("closeImport.handover.notEntering")}</span> : formatCount(locale, row.gioia365)}</td>
            <td>
              {row.explained.length === 0 && row.unexplained === 0 ? (
                <span className="g-ci__muted">{t("closeImport.handover.noDifference")}</span>
              ) : (
                <ul className="g-ci__why">
                  {row.unexplained > 0 ? (
                    <li data-tone="warning">
                      <strong>{t("closeImport.handover.unexplained", { count: formatCount(locale, row.unexplained) })}</strong>
                    </li>
                  ) : null}
                  {row.explained.map((item) => (
                    <li key={item.reason}>{t(`closeImport.handover.reason.${item.reason}`, { count: formatCount(locale, item.count) })}</li>
                  ))}
                </ul>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  );
}

type SavedSearchLeft = Readonly<{
  id: string | null;
  name: string;
  reason: CloseSavedSearchReason | null;
  decision: Readonly<{ kind: CloseSavedSearchDecision; by: string; at: string }> | null;
}>;

/**
 * Le ricerche salvate di Close che non sono diventate Smart List (22/10/2026): il nome e il motivo e, per ognuna, la decisione
 * (23/10/2026, INGR-28): «Rifatta a mano» o «Lasciata perdere», con chi e quando, e «Annulla». Una decisa non ferma più la disdetta. Un
 * nome non è un dato dei clienti; i filtri di Close non arrivano mai qui. Una verifica di prima ha soltanto i nomi, senza pulsanti.
 */
function SavedSearchNames({ left, total }: { left: readonly SavedSearchLeft[]; total: number }) {
  const t = useT();
  const { locale } = useLocale();
  const titleId = useId();
  const tab = useTab();
  const toast = useToast();
  // Una decisione alla volta per tutto l'elenco: ogni risposta è la verifica intera, e due risposte fuori ordine si coprirebbero.
  const decide = useCloseSavedSearchDecision(tab);
  const send = (id: string, decision: CloseSavedSearchDecision | null) => {
    if (decide.isPending) return;
    decide.mutate({ id, decision }, { onError: (error) => toast.show(messageForError(t, error), "warning") });
  };
  if (left.length === 0) return null;
  return (
    <div className="g-ci__nightly-part" role="group" aria-labelledby={titleId}>
      <h3 id={titleId}>{t("closeImport.handover.savedSearchesTitle")}</h3>
      <p className="g-ci__muted">{t("closeImport.handover.savedSearchesText")}</p>
      <ul className="g-ci__why g-ci__saved-searches">
        {left.map((item, index) => (
          <SavedSearchItem key={item.id ?? `${index}-${item.name}`} item={item} busy={decide.isPending} send={send} />
        ))}
        {total > left.length ? <li className="g-ci__muted">{t("closeImport.handover.savedSearchesMore", { count: formatCount(locale, total - left.length) })}</li> : null}
      </ul>
    </div>
  );
}

function SavedSearchItem({ item, busy, send }: { item: SavedSearchLeft; busy: boolean; send: (id: string, decision: CloseSavedSearchDecision | null) => void }) {
  const t = useT();
  const access = useAccess();
  const { id } = item;
  const name = item.name === "" ? t("closeImport.handover.savedSearchUnnamed") : item.name;
  const label = item.reason === null ? name : `${name} · ${t(`closeImport.handover.savedSearchReason.${item.reason}`)}`;
  const decisionName = (kind: CloseSavedSearchDecision) => t(kind === "rebuilt" ? "closeImport.handover.savedSearchRebuilt" : "closeImport.handover.savedSearchDropped");
  return (
    <li data-decided={item.decision === null ? undefined : "true"}>
      <span>{label}</span>
      {id === null ? null : (
        <span className="g-ci__saved-search-actions" role="group" aria-label={t("closeImport.handover.savedSearchActions", { name })}>
          {item.decision === null ? (
            <>
              <Button onClick={() => send(id, "rebuilt")} disabled={busy}>
                {t("closeImport.handover.savedSearchRebuilt")}
              </Button>
              <Button onClick={() => send(id, "dropped")} disabled={busy}>
                {t("closeImport.handover.savedSearchDropped")}
              </Button>
            </>
          ) : (
            <>
              <span className="g-ci__muted">
                {t("closeImport.handover.savedSearchDecided", { decision: decisionName(item.decision.kind), name: item.decision.by, ...whenParts(access.context.settings.timeZone, item.decision.at) })}
              </span>
              <Button variant="ghost" onClick={() => send(id, null)} disabled={busy}>
                {t("closeImport.handover.savedSearchUndo")}
              </Button>
            </>
          )}
        </span>
      )}
    </li>
  );
}

type CopyFactKey =
  | "audioWithAudio"
  | "audioCopied"
  | "audioToCopy"
  | "audioFailed"
  | "audioMissing"
  | "attachmentsTotal"
  | "attachmentsCopied"
  | "attachmentsToCopy"
  | "attachmentsFailed"
  | "attachmentsMissing";

/** Lo stato di una copia da Close (l'audio o gli allegati): quanti, copiati, da copiare, non riusciti, non più in Close. */
function CopyFacts({ title, off, facts }: { title: string; off: string | null; facts: ReadonlyArray<{ key: CopyFactKey; value: number }> }) {
  const t = useT();
  const { locale } = useLocale();
  const titleId = useId();
  return (
    <div className="g-ci__nightly-part" role="group" aria-labelledby={titleId}>
      <h3 id={titleId}>{title}</h3>
      {off === null ? null : <Reason tone="warning">{off}</Reason>}
      <dl className="g-ci__facts g-ci__audio">
        {facts.map((fact) => (
          <div key={fact.key}>
            <dt>{t(`closeImport.handover.${fact.key}`)}</dt>
            <dd>{formatCount(locale, fact.value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
